import React, { useState, useCallback } from 'react';
import { AutoComplete, Input, InputProps } from 'antd';
import axios from 'axios';
import { useLazyGetCountriesBySearchQuery } from 'lib.store/src/main/countries/countries.api';
import { useLazyGetCitiesBySearchQuery } from 'lib.store/src/main/cities/cities.api';
import { debounce } from 'lib.shared/src/utils';
import { EventBilling } from 'lib.store/src/type-organizer/billing/models';
import { INIT_CURRENT_PAGE } from 'lib.shared/src/constants';
import { GEOAPIFY_API_KEY } from './constants';

export interface AddressOption {
  value: string;
  label?: string;
  city?: string;
  country?: string;
  postcode?: string;
}

type Props = {
  placeholder?: string;
  value?: string;
  onSelect: (value: Partial<EventBilling>) => void;
  onBlur?: () => void;
  'data-test-id'?: string;
} & InputProps;

const AddressInput: React.FC<Props> = ({
  value,
  onSelect,
  onBlur,
  placeholder,
  style,
  id,
  className,
  'data-test-id': dataTestId,
}) => {
  const [options, setOptions] = useState<AddressOption[]>([]);

  const [inputValue, setInputValue] = useState<string>(value || '');

  const [getCountriesBySearch] = useLazyGetCountriesBySearchQuery();
  const [getCitiesBySearch] = useLazyGetCitiesBySearchQuery();

  const fetchAddressSuggestions = useCallback(async (query: string) => {
    if (!query) return;
    try {
      const response = await axios.get('https://api.geoapify.com/v1/geocode/autocomplete', {
        params: {
          text: query,
          apiKey: GEOAPIFY_API_KEY,
          limit: 5,
        },
      });

      const suggestions = response.data.features
        .map((feature: any) => ({
          value: feature.properties.formatted,
          label: feature.properties.formatted,
          postcode: feature.properties.postcode,
          city: feature.properties.city,
          country: feature.properties.country,
        }))
        .filter(({ city }: AddressOption) => city);

      setOptions(suggestions);
    } catch (error) {
      console.error('Address error:', error);
      setOptions([]);
    }
  }, []);

  const debouncedFetchAddressSuggestions = useCallback(
    debounce((value: string) => {
      fetchAddressSuggestions(value);
    }, 300),
    [fetchAddressSuggestions],
  );

  const handleChange = (value: string) => {
    setInputValue(value);
    debouncedFetchAddressSuggestions(value);
  };

  const handleSelect = async (value: string, option: AddressOption) => {
    setInputValue(value);

    try {
      const countryResponse = await getCountriesBySearch({ search: option.country || '' }).unwrap();
      const countryId = countryResponse?.data?.[0]?.id || '';
      const countryResult = countryResponse?.data?.[0] || null;
      const cityResponse = await getCitiesBySearch({
        search: option.city || '',
        countries: countryId.toString(),
        page: INIT_CURRENT_PAGE,
      }).unwrap();
      const cityResult = cityResponse?.data?.[0]
        ? { ...(cityResponse?.data?.[0] || {}), country: countryResult }
        : null;

      const newData: Partial<EventBilling> = {
        city: cityResult,
        country: countryResult,
        address: value,
      };
      if (option.postcode) {
        newData.postal_code = option.postcode;
      }
      onSelect(newData);
    } catch (error) {
      console.error('Address error:', error);
    }
  };

  const handleBlur = () => {
    onSelect({ address: inputValue });
    if (onBlur) {
      onBlur();
    }
  };

  return (
    <AutoComplete
      id={id}
      options={options}
      value={inputValue}
      onChange={handleChange}
      onSelect={handleSelect}
      onBlur={handleBlur}
      placeholder={placeholder || 'Organization address'}
      filterOption={false}
      style={{ width: '100%' }}
      className={className || ''}
    >
      <Input style={style} defaultValue={inputValue} data-test-id={dataTestId} />
    </AutoComplete>
  );
};

export default AddressInput;
